import { Order, QueryEntity } from '@datorama/akita';
import {
	compareEntities,
	compareEntity,
	isEquals,
} from '@lycoris-nubila/ngx-lycoris';
import { isEqual, isNil, reject, uniqBy } from 'lodash-es';
import isEmpty from 'lodash-es/isEmpty';
import { Observable, of } from 'rxjs';
import {
	distinctUntilChanged,
	first,
	map,
	shareReplay,
	tap,
} from 'rxjs/operators';

import { Organization } from 'organization/organization';
import { UserRole } from 'user/model/user-role';
import { UserRepositoryPort } from 'user/port/user-repository.port';
import { ObserveSearchedCoachesIncludingPartnersQuery } from 'user/query/observe-searched-coaches-including-partners.query';
import { User } from 'user/user';
import { UserState, UserStore } from 'user/user.store';

import { CacheObservable } from '../../app-decorators';

export class UserRepositoryAdapter
	extends QueryEntity<UserState>
	implements UserRepositoryPort
{
	constructor(
		protected store: UserStore,
		private organizations$: (ids: string[]) => Observable<Organization[]>
	) {
		super(store);
	}

	public removeUserCoach(userId: string): void {
		this.store.update(userId, { coachId: null });
	}

	public findById(id: string): Promise<User> {
		return id
			? this.selectEntity(id)
					.pipe(
						map((document) => this.store.asEntity(document, this.organizations$)),
						first()
					)
					.toPromise()
			: Promise.resolve(null as User);
	}

	@CacheObservable()
	public observeSearchedCoachesIncludingPartners(
		query: ObserveSearchedCoachesIncludingPartnersQuery
	): Observable<User[]> {
		return this.selectAll({
			filterBy: (entity) =>
				isEquals(entity.role, UserRole.COACH) &&
				(!query.organizationId ||
					isEquals(entity.organizationId, query.organizationId) ||
					(entity.partnersId || []).includes(query.organizationId)) &&
				(isEmpty(query.sectors) ||
					new RegExp(`[${query.sectors.join('')}]`, 'gmi').test(
						entity.companySectors
					)) &&
				(!query.name ||
					new RegExp(query.name, 'gmi').test(entity.lastName) ||
					new RegExp(query.name, 'gmi').test(entity.firstName)),
			sortByOrder: Order.ASC,
			sortBy: 'lastName',
		}).pipe(
			map((documents) =>
				documents.map((document) =>
					this.store.asEntity(document, this.organizations$)
				)
			),
			distinctUntilChanged(compareEntities),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	@CacheObservable()
	public observeUserById(id: string): Observable<User> {
		return id
			? this.selectEntity(id).pipe(
					distinctUntilChanged(isEqual),
					map((document) => this.store.asEntity(document, this.organizations$)),
					shareReplay({ bufferSize: 1, refCount: true })
			  )
			: of(null);
	}

	@CacheObservable()
	public observeUserByIds(ids: string[]): Observable<User[]> {
		return this.selectAll({
			filterBy: (entity) => (ids || []).includes(entity.id),
			sortByOrder: Order.ASC,
			sortBy: 'lastName',
		}).pipe(
			map((documents) =>
				documents.map((document) =>
					this.store.asEntity(document, this.organizations$)
				)
			),
			distinctUntilChanged(compareEntities),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	@CacheObservable()
	public observeUsersByCoachId(coachId: string): Observable<User[]> {
		return coachId
			? this.selectAll({
					filterBy: (entity) => entity.coachId === coachId,
					sortByOrder: Order.ASC,
					sortBy: 'lastName',
			  }).pipe(
					map((documents) =>
						documents.map((document) =>
							this.store.asEntity(document, this.organizations$)
						)
					),
					distinctUntilChanged(compareEntities),
					shareReplay({ bufferSize: 1, refCount: true })
			  )
			: of([]);
	}

	@CacheObservable()
	public observeCoacheesWithoutCoach(
		organizationsId: string[]
	): Observable<User[]> {
		return this.selectAll({
			filterBy: (entity) =>
				isEquals(entity.role, UserRole.COACHEE) &&
				(organizationsId || []).includes(entity.organizationId) &&
				isNil(entity.coachId),
			sortByOrder: Order.ASC,
			sortBy: 'lastName',
		}).pipe(
			map((documents) =>
				documents.map((document) =>
					this.store.asEntity(document, this.organizations$)
				)
			),
			distinctUntilChanged(compareEntities),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public async saveUsers(entities: User[]): Promise<void> {
		this.store.upsertMany(
			uniqBy(
				reject(entities, isNil).map((e) => this.store.asDocument(e)),
				'id'
			)
		);
	}
}
